import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { FiChevronRight } from 'react-icons/fi';
import { Checkbox, Modal } from 'antd';
import { BsCheckLg } from 'react-icons/bs';
import {
  CredusButton,
  CredusInput,
  CredusSelect,
  CredusText,
  OptionType,
  color,
} from 'ui';
import moment from 'moment';

import { DivProps, BulletProps } from '../TestReUpload';
import { TestContext } from '../../../../../contexts/TestContext';
import { useQuery } from 'react-query';
import institutionApi from '../../../../../api/institution';

interface TestConfigurationProps {
  configureTest: boolean;
  setConfigureTest: React.Dispatch<React.SetStateAction<boolean>>;
  setTestReUpload: React.Dispatch<React.SetStateAction<boolean>>;
  setPublishTest: React.Dispatch<React.SetStateAction<boolean>>;
}

const TestConfigurationModal = ({
  configureTest,
  setConfigureTest,
  setTestReUpload,
  setPublishTest,
}: TestConfigurationProps) => {
  const { setTestData, testData } = useContext(TestContext);
  const [dateTime, setDateTime] = useState('');
  const [timeZone, setTimeZone] = useState<string | undefined>('');
  const [duration, setDuration] = useState({ hrs: '', min: '' });
  const totalTestPoints = testData.questions
    .map((question) => question.point)
    .reduce((a, b) => a + b);
  const [point, setPoint] = useState({
    maxScore: totalTestPoints,
    passMark: 0,
  });
  const [maxAttempt, setMaxAttempt] = useState(0);
  const [passMarkUnit, setPassMarkUnit] = useState(1);

  const { data } = useQuery(
    [
      'getTimeZone'
    ],
    () =>
      institutionApi.getTimeZone()
  );

  const passMarkUnits = [
    {
      optionName: 'Points',
      optionValue: 'points',
      optionId: '1',
    },
    {
      optionName: 'Percentage',
      optionValue: 'percentage',
      optionId: '2',
    },
  ];

  const handlePublish = () => {
    const date = moment(dateTime).format('MM/DD/yyyy, h:mm:ss a').split(',')[0];
    const time = moment(dateTime)
      .format('MMMM Do YYYY, h:mm:ss a')
      .split(',')[1];
    const { hrs, min } = duration;
    const { maxScore, passMark } = point;
    const newDuration = `${hrs ? hrs : '0'}Hrs ${min ? min : '0'}Mins`;
    setTestData((prev) => ({
      ...prev,
      date,
      time,
      timeZone,
      maxAttempt,
      passMarkUnit,
      maxScore,
      passMark: passMarkUnit == 2 ? (passMark / 100) * maxScore : passMark,
      duration: newDuration,
    }));
    setTestReUpload(false);
    setConfigureTest(false);
    setPublishTest(true);
  };

  const handleDateTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateTime(e.target.value);
  };

  const handleTimeZone = (value: string | undefined) => {
    setTimeZone(value);
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDuration((prev) => ({ ...prev, [name]: value }));
  };
  const handlePointChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPoint((prev) => ({ ...prev, [name]: parseInt(value) }));
  };

  const handleMaxAttempt = (value: string | undefined) => {
    if (value) setMaxAttempt(parseInt(value));
  };

  const handlePassMarkUnit = (value: string) => {
    setPassMarkUnit(parseInt(value));
  };

  return (
    <Modal open={configureTest} footer="" width="60%" closable={false}>
      <Container>
        <Div>
          <BulletDiv>
            <Bullet
              background={color.white}
              border={`1px solid ${color.primary}`}
            >
              <BsCheckLg color={color.primary} />
            </Bullet>
            <CredusText size="md">Upload</CredusText>
          </BulletDiv>
          <FiChevronRight color="#000929" />
          <BulletDiv>
            <Bullet background={color.primary}>
              <CredusText size="xs" color={color.white}>
                2
              </CredusText>
            </Bullet>
            <CredusText size="md">Configure</CredusText>
          </BulletDiv>
          <FiChevronRight color="#000929" />
          <BulletDiv>
            <Bullet background={color.lightPurple}>
              <CredusText size="xs">3</CredusText>
            </Bullet>
            <CredusText size="md">Review & Publish</CredusText>
          </BulletDiv>
        </Div>
        <Body>
          {/* <Top>
            <Div gap="2.5rem">
              <IoSettingsOutline color="#CCCCCC" size={'43px'} />
              <div>
                <CredusText
                  size="sm"
                  weight="medium"
                  style={{ marginBottom: '.2rem' }}
                >
                  Set Configurations to Default
                </CredusText>
                <CredusText size="xs" color="rgba(0, 0, 0, 0.6);">
                  Once toggle off, Configure is not set as default, hence you
                  can configure settings that will apply to this test.{' '}
                </CredusText>
              </div>
            </Div>
            <Switch />
          </Top> */}
          <Bottom>
            <Div>
              <BottomLeft>
                <CredusText size="sm" weight="medium">
                  Set Date & Time
                </CredusText>
              </BottomLeft>
              <BottomRight>
                <CredusInput
                  type="datetime-local"
                  onChange={handleDateTime}
                  name="dateTime"
                />
              </BottomRight>
            </Div>
            <Div>
              <BottomLeft>
                <CredusText
                  size="sm"
                  weight="medium"
                  style={{ marginTop: '-2rem' }}
                >
                  Set Time Zone
                
                </CredusText>
              </BottomLeft>
              <BottomRight>
                <CredusSelect
                  options={(data?.data ?? []).map((timeZone: {optionText: string}) => timeZone.optionText)}
                  onChange={(value) => handleTimeZone(value)}
                  placeholder="(GMT + 1:00) West Africa Time(Nigeria)"
                />
                <Div>
                  <Checkbox />
                  <CredusText size="sm" color="#000929">
                    Automatically synchronize or adjust time zone
                  </CredusText>
                </Div>
              </BottomRight>
            </Div>
            <Div>
              <BottomLeft>
                <CredusText size="sm" weight="medium">
                  Set Test duration
                </CredusText>
              </BottomLeft>
              <Grid>
                <TimeDiv>
                  <DurationInput
                    type="number"
                    name="hrs"
                    max={'12'}
                    defaultValue={'00'}
                    onChange={handleDurationChange}
                  />

                  <CredusText size="lg" color="#00092930">
                    HH
                  </CredusText>
                </TimeDiv>
                <TimeDiv>
                  {' '}
                  <DurationInput
                    type="number"
                    name="min"
                    max={'59'}
                    defaultValue={'00'}
                    onChange={handleDurationChange}
                  />
                  <CredusText size="lg" color="#00092930">
                    MM
                  </CredusText>
                </TimeDiv>
                <TimeDiv>
                  {' '}
                  <DurationInput
                    type="number"
                    name="sec"
                    max={'59'}
                    defaultValue={'00'}
                    onChange={handleDurationChange}
                  />
                  <CredusText size="lg" color="#00092930">
                    SEC
                  </CredusText>
                </TimeDiv>
              </Grid>
            </Div>
            <Div>
              <BottomLeft>
                <CredusText size="sm" weight="medium">
                  Total Test Points
                </CredusText>
              </BottomLeft>

              <div
                style={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ width: '7rem' }}>
                  <CredusInput
                    placeholder="100pts"
                    value={totalTestPoints}
                    onChange={handlePointChange}
                    name="maxScore"
                  />
                </div>

                <div style={{ display: 'flex', gap: '2rem' }}>
                  <CredusText size="sm" weight="medium">
                    Pass Mark
                  </CredusText>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <div style={{ width: '5rem' }}>
                      <CredusInput
                        onChange={handlePointChange}
                        name="passMark"
                      />
                    </div>
                    <div style={{ width: '7rem' }}>
                      <CredusSelect
                        options={passMarkUnits}
                        onChange={(value) =>
                          handlePassMarkUnit(
                            passMarkUnits?.filter(
                              (el: OptionType) => el.optionValue == value
                            )[0]?.optionId
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Div>
            <Div>
              <BottomLeft>
                <CredusText size="sm" weight="medium">
                  No. of Attempts
                </CredusText>
              </BottomLeft>
              <div style={{ width: '100%' }}>
                <div style={{ width: '7rem' }}>
                  <CredusSelect
                    options={['1', '2', '3', '4', '5']}
                    onChange={(value) => handleMaxAttempt(value)}
                  />
                </div>
              </div>
            </Div>
          </Bottom>
          <BtnDiv>
            <CredusButton
              variant="secondary"
              onClick={() => setConfigureTest(false)}
            >
              Return
            </CredusButton>
            <CredusButton onClick={handlePublish}>
              Review & Publish
            </CredusButton>
          </BtnDiv>
        </Body>
      </Container>
    </Modal>
  );
};

export default TestConfigurationModal;

const Container = styled.div`
  padding: 1rem;
`;

const BtnDiv = styled.div`
  width: 80%;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const Div = styled.div<DivProps>`
  display: flex;
  gap: ${({ gap }) => (gap ? gap : '1rem')};
  width: ${({ width }) => (width ? width : '100%')};
  justify-content: ${({ justify }) => justify};
  align-items: center;
`;

const BulletDiv = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const Bullet = styled.div<BulletProps>`
  height: 28px;
  width: 28px;
  border-radius: 50%;
  background: ${(props) => props.background};
  color: ${(props) => props.color};
  border: ${(props) => props.border};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Body = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 4.5rem;
  align-items: center;
  gap: 1rem;
`;

const Bottom = styled.div`
  width: 80%;
  box-sizing: border-box;
  min-height: 328px;
  border-radius: 5px;
  /* border: 1.5px solid #e0def7; */
  gap: 2rem;
  padding: 2rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 3rem;
  justify-content: center;
`;

const BottomLeft = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 35%;
`;
const BottomRight = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;
const Grid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 1rem;
`;
const TimeDiv = styled.div`
  width: 100%;
  border: 1px solid #e0def7;
  border-radius: 8px;
  height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
`;

const DurationInput = styled.input`
  border: none;
  outline: none;
  width: 40%;
`;
